<template>
  <div class="header-actions">
    <!-- Theme Toggle -->
    <div class="toggle-container" :class="{ 'is-ready': isReady }">
      <input type="checkbox" class="toggle-input" :checked="isDarkMode" @change="toggleTheme">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 292 142" class="toggle">
        <!-- Declared before anything points at it, and inside defs.

             It sat at the end, after the group that references it. A filter
             the parser has not reached yet does not exist, and an element
             referencing a filter that does not exist is not painted at all,
             so while the document was still streaming the knob was missing
             and then appeared. Reading order is the whole fix. -->
        <defs>
          <!-- A fixed region, in user space, rather than one derived from the group.
               Left to itself a filter region is the bounding box of what it filters,
               and this group has no settled bounding box: the left blob scales to
               nothing as the right one scales up, and the bar slides 150 units across.
               Every one of those changes the box, and a changed region means the
               filter is derived and rasterised again. On Chrome that shows as the knob
               blinking out and back. Pinned here, so none of it moves the region.
          
               40 units of margin covers the blur, which reaches about three times its
               stdDeviation of 10. -->
          <filter
            id="goo"
            filterUnits="userSpaceOnUse"
            x="-40"
            y="-40"
            width="372"
            height="222"
          >
            <feGaussianBlur stdDeviation="10" result="blur" in="SourceGraphic"/>
            <feColorMatrix result="goo" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 18 -7" mode="matrix" in="blur"/>
          </filter>
        </defs>

        <path d="M71 142C31.7878 142 0 110.212 0 71C0 31.7878 31.7878 0 71 0C110.212 0 119 30 146 30C173 30 182 0 221 0C260 0 292 31.7878 292 71C292 110.212 260.212 142 221 142C181.788 142 173 112 146 112C119 112 110.212 142 71 142Z" class="toggle-background"/>
        <rect rx="6" height="64" width="12" y="39" x="64" class="toggle-icon on"/>
        <path d="M221 91C232.046 91 241 82.0457 241 71C241 59.9543 232.046 51 221 51C209.954 51 201 59.9543 201 71C201 82.0457 209.954 91 221 91ZM221 103C238.673 103 253 88.6731 253 71C253 53.3269 238.673 39 221 39C203.327 39 189 53.3269 189 71C189 88.6731 203.327 103 221 103Z" fill-rule="evenodd" class="toggle-icon off"/>
        <g filter="url(#goo)">
          <rect fill="#fff" rx="29" height="58" width="116" y="42" x="13" class="toggle-circle-center"/>
          <rect fill="#fff" rx="58" height="114" width="114" y="14" x="14" class="toggle-circle left"/>
          <rect fill="#fff" rx="58" height="114" width="114" y="14" x="164" class="toggle-circle right"/>
        </g>
      </svg>
    </div>

    <!-- Contact Button -->
    <div class="contact-button-wrapper">
      <NuxtLink to="/#contact" class="contact-button" @click="handleContactClick">
        <span class="button-text">CONTACT</span>
        <div class="button-arrow">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3.33334 8H12.6667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M8 3.33337L12.6667 8.00004L8 12.6667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </div>
      </NuxtLink>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';

const isDarkMode = ref(false);
// Gates the toggle's CSS transitions until after the first paint, so syncing
// the switch to the already-applied theme doesn't animate across on load.
const isReady = ref(false);
const { navigateToPanel } = usePanelNav();

function handleContactClick(event) {
  navigateToPanel(event, '/#contact');
}

function toggleTheme() {
  isDarkMode.value = !isDarkMode.value;

  if (isDarkMode.value) {
    document.documentElement.classList.add('dark');
    localStorage.setItem('theme', 'dark');
  } else {
    document.documentElement.classList.remove('dark');
    localStorage.setItem('theme', 'light');
  }
}

onMounted(() => {
  // The theme class is already on <html> — the inline head script in
  // nuxt.config applies it before first paint. Just mirror it into the
  // switch's state rather than re-deriving (and re-applying) it here.
  isDarkMode.value = document.documentElement.classList.contains('dark');

  // Two frames, not one. The state above changes what the switch is showing,
  // and this is what allows it to animate; a single frame between them lands
  // in the same style pass often enough that the switch slides across on load
  // instead of simply appearing in place. Every rect it moves sits inside an
  // SVG Gaussian blur, so that slide is also the most expensive thing on the
  // page while it runs.
  //
  // The same shape as the theme-ready gate in nuxt.config, for the same reason.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      isReady.value = true;
    });
  });
});
</script>

<style lang="scss" scoped>
.header-actions {
  position: fixed;
  top: clamp(1rem, 3vh, 1.5rem);
  right: clamp(1rem, 3vw, 2rem);
  z-index: 2000;
  display: flex;
  align-items: center;
  gap: 1rem;

  * {
    user-select: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
  }
}

// Mode Toggle
.toggle-container {
  --active-color: var(--accent);
  --inactive-color: rgba(var(--muted-rgb), 0.3);
  // Required: .toggle-input is absolutely positioned at 100%×100%, so without
  // a positioned ancestor here it sized itself against .header-actions
  // (position: fixed) instead — stretching the invisible checkbox across the
  // whole cluster and swallowing clicks meant for the Contact button.
  position: relative;
  aspect-ratio: 292 / 142;
  height: 2em;
  width: 4.1em;

  // Until the switch has been synced to the current theme (one frame after
  // mount) suppress its transitions, so it appears already in the correct
  // position instead of sliding there on every page load.
  &:not(.is-ready) * {
    transition: none !important;
  }

  @media (max-width: 1280px) {
    height: 1.6em;
    width: 3.3em;
  }

  @media (max-width: 768px) {
    height: 1.5em;
    width: 3.1em;
  }
}

.toggle-input {
  appearance: none;
  margin: 0;
  position: absolute;
  z-index: 1;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  cursor: pointer;
}

.toggle {
  width: 100%;
  height: 100%;
  overflow: visible;
}

.toggle-background {
  fill: var(--inactive-color);
  transition: fill .4s;
}

.toggle-input:checked + .toggle .toggle-background,
:root.dark .toggle .toggle-background {
  fill: var(--active-color);
}

.toggle-circle-center {
  transform-origin: center;
  transition: transform .6s;
}

.toggle-input:checked + .toggle .toggle-circle-center,
:root.dark .toggle .toggle-circle-center {
  transform: translateX(150px);
}

.toggle-circle {
  transform-origin: center;
  transition: transform .45s;
  backface-visibility: hidden;
}

.toggle-circle.left {
  transform: scale(1);
}

.toggle-input:checked + .toggle .toggle-circle.left,
:root.dark .toggle .toggle-circle.left {
  transform: scale(0);
}

.toggle-circle.right {
  transform: scale(0);
}

.toggle-input:checked + .toggle .toggle-circle.right,
:root.dark .toggle .toggle-circle.right {
  transform: scale(1);
}

.toggle-icon {
  transition: fill .4s;
}

.toggle-icon.on {
  fill: var(--inactive-color);
}

.toggle-input:checked + .toggle .toggle-icon.on,
:root.dark .toggle .toggle-icon.on {
  fill: #fff;
}

.toggle-icon.off {
  fill: #eaeaec;
}

.toggle-input:checked + .toggle .toggle-icon.off,
:root.dark .toggle .toggle-icon.off {
  fill: var(--active-color);
}

// Contact Button
.contact-button-wrapper {
  position: relative;

  @media (max-width: 1280px) {
    transform: scale(0.9);
  }
}

.contact-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1.2rem;
  background: transparent;
  border: 1px solid var(--accent);
  color: var(--text);
  font-weight: 600;
  font-size: 0.9rem;
  border-radius: 50px;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  font-family: 'Orbitron', sans-serif;
  letter-spacing: 1px;
  box-shadow: 0 0 10px rgba(var(--accent-rgb), 0.1);

  @media (max-width: 1280px) {
    padding: 0.4rem 1rem;
    font-size: 0.8rem;
    letter-spacing: 0.8px;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg,
      transparent,
      rgba(var(--accent-rgb), 0.1),
      transparent
    );
    transition: left 0.3s ease;
  }

  .button-text {
    position: relative;
    z-index: 1;
    background: linear-gradient(90deg, var(--text), var(--accent));
    background-size: 200% auto;
    background-clip: text;
    -webkit-background-clip: text;
    color: transparent;
  }

  .button-arrow {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    transition: transform 0.3s ease;
    color: var(--accent);

    @media (max-width: 1280px) {
      transform: scale(0.9);
    }
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 0 15px rgba(var(--accent-rgb), 0.2);
    border-color: rgba(var(--accent-rgb), 0.8);

    &::before {
      left: 100%;
    }

    .button-arrow {
      transform: translateX(3px);

      @media (max-width: 1280px) {
        transform: translateX(2px) scale(0.9);
      }
    }
  }

  &:active {
    transform: translateY(1px);
    box-shadow: 0 0 8px rgba(var(--accent-rgb), 0.2);
  }

  @media (max-width: 768px) {
    display: none;
  }
}
</style>
